import axios from 'axios';
import { getJson, sendJson } from '@/lib/api';
import { authHeader, carrierLabel, type Shipment } from '@/lib/events';

/** One place an item can be bought. */
export interface SupplySource {
  readonly vendor: string;
  readonly url?: string;
  readonly sku?: string;
  readonly note?: string;
}

/** A catalog item: what BCE packs, with a picture and where to reorder it. */
export interface SupplyItem {
  readonly _id: string;
  readonly name: string;
  readonly description?: string;
  readonly imageUrl?: string;
  readonly sources: readonly SupplySource[];
  readonly unit?: string;
  readonly notes?: string;
  readonly isActive: boolean;
  readonly updatedAt?: string;
}
export type SupplyItemWrite = Omit<SupplyItem, '_id' | 'updatedAt'>;

/** So many for every N participants, rounded up. */
export interface PerGroupRule {
  readonly quantity: number;
  readonly participants: number;
}
/** A band of the headcount; no `upTo` means "and above". */
export interface RangeRule {
  readonly upTo?: number;
  readonly quantity: number;
}
export interface QuantityRule {
  readonly fixed?: number;
  readonly perTeam?: number;
  readonly perParticipant?: number;
  readonly perGroup?: PerGroupRule;
  readonly ranges?: readonly RangeRule[];
}

/** One line of a program's default kit. */
export interface KitLine {
  readonly itemId: string;
  readonly quantityRule: QuantityRule;
  readonly optional?: boolean;
  readonly note?: string;
}

/** One line of an event's packing list. */
export interface PackingLine {
  readonly _id: string;
  readonly itemId?: string;
  readonly name: string;
  readonly quantity: number;
  readonly unit?: string;
  readonly imageUrl?: string;
  readonly sources?: readonly SupplySource[];
  readonly optional?: boolean;
  readonly note?: string;
  readonly source: 'kit' | 'manual';
  readonly needsHeadcount?: boolean;
  readonly clientProvides?: boolean;
  readonly packedAt?: string;
  /** The shipments this line travels in; a line can be split across several. */
  readonly shipments?: readonly PackingShipment[];
}

/** One shipment a packing line travels in, and how many of it. */
export interface PackingShipment {
  readonly shipmentId: string;
  readonly quantity?: number;
}

export async function listSupplyItems(signal?: AbortSignal): Promise<readonly SupplyItem[]> {
  return getJson<SupplyItem[]>('/supplies', undefined, signal);
}
export async function readSupplyItem(id: string, signal?: AbortSignal): Promise<SupplyItem> {
  return getJson<SupplyItem>(`/supplies/${id}`, undefined, signal);
}
export async function createSupplyItem(body: SupplyItemWrite): Promise<SupplyItem> {
  return sendJson<SupplyItem>('post', '/supplies', body);
}
export async function updateSupplyItem(id: string, body: Partial<SupplyItemWrite>): Promise<SupplyItem> {
  return sendJson<SupplyItem>('patch', `/supplies/${id}`, body);
}
export async function deleteSupplyItem(id: string): Promise<void> {
  await sendJson('delete', `/supplies/${id}`);
}
export async function saveProgramKit(programId: string, kit: readonly KitLine[]): Promise<readonly KitLine[]> {
  return sendJson<KitLine[]>('put', `/programs/${programId}/kit`, { kit });
}

export async function listPackingList(eventId: string, signal?: AbortSignal): Promise<readonly PackingLine[]> {
  const response = await axios.get<{ data: PackingLine[] }>(`/api/v3/events/${eventId}/packing-list`, { ...(signal ? { signal } : {}), headers: await authHeader() });
  return response.data.data;
}
export async function resetPackingList(eventId: string): Promise<readonly PackingLine[]> {
  const response = await axios.post<{ data: PackingLine[] }>(`/api/v3/events/${eventId}/packing-list/reset`, {}, { headers: await authHeader() });
  return response.data.data;
}
export async function addPackingLine(eventId: string, body: { itemId?: string; name?: string; quantity: number; unit?: string; note?: string }): Promise<readonly PackingLine[]> {
  const response = await axios.post<{ data: PackingLine[] }>(`/api/v3/events/${eventId}/packing-list`, body, { headers: await authHeader() });
  return response.data.data;
}
export async function updatePackingLine(eventId: string, lineId: string, body: { quantity?: number; note?: string; clientProvides?: boolean; packed?: boolean; shipments?: readonly PackingShipment[] }): Promise<readonly PackingLine[]> {
  const response = await axios.patch<{ data: PackingLine[] }>(`/api/v3/events/${eventId}/packing-list/${lineId}`, body, { headers: await authHeader() });
  return response.data.data;
}
export async function deletePackingLine(eventId: string, lineId: string): Promise<readonly PackingLine[]> {
  const response = await axios.delete<{ data: PackingLine[] }>(`/api/v3/events/${eventId}/packing-list/${lineId}`, { headers: await authHeader() });
  return response.data.data;
}

/** "UPS 1Z999, box 2 of 3": how a shipment reads in a materials list. */
export function shipmentLabel(shipment: Pick<Shipment, 'carrier' | 'trackingNumber' | 'pieces' | 'pieceType' | 'vendor'>): string {
  const head = [carrierLabel(shipment.carrier), shipment.trackingNumber].filter(Boolean).join(' ');
  const piece = shipment.pieces?.count ? `${shipment.pieceType ?? 'box'} ${shipment.pieces.count}${shipment.pieces.of ? ` of ${shipment.pieces.of}` : ''}` : '';
  return [head || shipment.vendor || 'Shipment', piece].filter(Boolean).join(', ');
}

/** "Crown Awards, Amazon" — the vendors a line can be reordered from. */
export function vendorsText(sources: readonly SupplySource[] | undefined): string {
  return (sources ?? []).map((source) => source.vendor).join(', ');
}

/** "3 per team + 2" — how a rule reads in the kit editor. */
export function ruleText(rule: QuantityRule): string {
  const bands = (rule.ranges ?? [])
    .slice()
    .sort((a, b) => (a.upTo ?? Number.MAX_SAFE_INTEGER) - (b.upTo ?? Number.MAX_SAFE_INTEGER))
    .map((band) => (band.upTo === undefined ? `${band.quantity} above` : `${band.quantity} up to ${band.upTo}`));
  const parts = [
    rule.fixed ? `${rule.fixed}` : null,
    rule.perTeam ? `${rule.perTeam} per team` : null,
    rule.perParticipant ? `${rule.perParticipant} per person` : null,
    rule.perGroup ? `${rule.perGroup.quantity} per ${rule.perGroup.participants} people` : null,
    bands.length ? `by headcount: ${bands.join(', ')}` : null,
  ].filter(Boolean);
  return parts.join(' + ') || '0';
}

/** "(33 sets) Medals" — the line the old supplies note used. */
export function packingLineText(line: Pick<PackingLine, 'quantity' | 'name' | 'unit'>): string {
  return `(${line.quantity}${line.unit ? ` ${line.unit}` : ''}) ${line.name}`;
}
